import { PublicationShortname } from "./serviceIdentifier";

/**
 * Service flags that mark a user as an active e-paper subscriber for each
 * paid publication.
 *
 * Single source of truth shared by two bundles: the edge
 * AuthSideEffectMiddleware gates the full reader on these (via
 * verifyServiceFlagWithPublication in EntitlementChecker, which re-imports this
 * const), and the client Home page reads the same mapping to decide
 * preview-vs-full CTA. Keeping ONE mapping is deliberate — a client copy that
 * drifted from the edge check would show the wrong CTA (e.g. preview to a
 * subscriber, or the full flow to a lapsed user).
 *
 * Typed as Partial<Record<string, string[]>> so an arbitrary pubId lookup
 * returns string[] | undefined (the runtime guard in isEntitledForPub is real —
 * free/unknown pubs have no entry). The middleware re-casts it to its own
 * Record<Publication, string[]> view without an enum-interop dance.
 */
export const SubscriberFlags: Readonly<Partial<Record<string, string[]>>> = {
  st: ["stp", "stp_trial", "st_epaper_lite"],
  bt: ["bts", "bts_trial", "bt_epaper_lite"],
  zb: ["zbs", "zbs_trial", "zb_epaper_lite"],
  bh: ["bh_online", "bh_online_trial", "bh_epaper_lite"],
  tm: ["cir_tm_online", "cir_tm_online_trial", "tm_epaper_lite"],
  sm: ["cir_sm_online", "cir_sm_online_trial", "sm_epaper_lite"],
};

/**
 * Client-side entitlement check mirroring the edge middleware's
 * verifyServiceFlagWithPublication. `svc` is the "|"-joined service_flags
 * string carried on the visitorContext (see mergeServiceFlagsWithDelimitor in
 * trackers/viewerContext). Returns false for free/unknown pubs (no flags) and
 * for an empty/undefined svc (anonymous or unresolved).
 */
export function isEntitledForPub(
  svc: string | undefined,
  pubId: PublicationShortname | null,
): boolean {
  if (!svc || !pubId) {
    return false;
  }
  const subscriberFlags = SubscriberFlags[pubId];
  if (!subscriberFlags) {
    return false;
  }
  const userFlags = svc.split("|");
  return subscriberFlags.some((flag) => userFlags.includes(flag));
}
